/**
 * Copyright (c) 2022 Animation Studio Voronezh.
 *
 * CONFIDENTIAL AND PROPRIETARY
 *
 * This work is provided "AS IS" and subject to the Prodtrack
 * Source Code License. By accessing, using, copying or modifying
 * this work you indicate your agreement to the Prodtrack Source Code License.
 * All rights not expressly granted therein are reserved by Animation Studio Voronezh.
 */

import React from "react";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import ColorsMap from "../../views/ColorsMap";
import { buttonPalette } from "./buttonColors";
import { CssBaseline } from "@mui/material";
import { tablePalette } from "./newTableColors";

const createThemeWithColorMap = (theme) =>
    createTheme(theme, {
        palette: {
            ...Object.entries(ColorsMap).reduce(
                (output, [name, color]) => ({
                    ...output,
                    [name]: {
                        main: color,
                        contrastText: theme.palette.getContrastText(color),
                    },
                }),
                {}
            ),
        },
    });

const lightTheme = createThemeWithColorMap(
    createTheme({
        palette: {
            background: { main: "white" },
            buttonPrimary: buttonPalette.primary.light,
            buttonSuccess: buttonPalette.success.light,
            buttonError: buttonPalette.error.light,
            buttonDefault: buttonPalette.default.light,

            selectedRow: tablePalette.default.light.selectedRow,
            selectedGroupingRow: tablePalette.default.light.selectedGroupingRow,

            tableHeader: tablePalette.default.light.headerGray,
            tableGroupingRow: tablePalette.default.light.groupingGray,
            tableRow: tablePalette.default.light.rowGray,
            ganttWeekend: tablePalette.default.light.ganttWeekend,
            ganttToday: tablePalette.default.light.ganttToday,
            ganttTaskLink: tablePalette.default.light.ganttTaskLink,
            ganttTask: tablePalette.default.light.ganttTask.primary,
            ganttTaskGroup: tablePalette.default.light.ganttTaskGroup.primary,
            ganttMilestone: tablePalette.default.light.ganttMilestone,
        },
    })
);

const darkTheme = createThemeWithColorMap(
    createTheme({
        palette: {
            mode: "dark",
            background: { main: "#3b3b3b" },
            buttonPrimary: buttonPalette.primary.dark,
            buttonSuccess: buttonPalette.success.dark,
            buttonError: buttonPalette.error.dark,
            buttonDefault: buttonPalette.default.dark,

            selectedRow: tablePalette.default.dark.selectedRow,
            selectedGroupingRow: tablePalette.default.dark.selectedGroupingRow,

            tableHeader: tablePalette.default.dark.headerGray,
            tableGroupingRow: tablePalette.default.dark.groupingGray,
            tableRow: tablePalette.default.dark.rowGray,
            ganttWeekend: tablePalette.default.dark.ganttWeekend,
            ganttToday: tablePalette.default.dark.ganttToday,
            ganttTaskLink: tablePalette.default.dark.ganttTaskLink,
            ganttTask: tablePalette.default.dark.ganttTask.primary,
            ganttTaskGroup: tablePalette.default.dark.ganttTaskGroup.primary,
            ganttMilestone: tablePalette.default.dark.ganttMilestone,
        },
    })
);

const ThemeContextProvider = ({ children }) => {
    const isDarkTheme = useSelector((state) => state.isDarkTheme);
    return (
        <ThemeProvider theme={isDarkTheme ? darkTheme : lightTheme}>
            <CssBaseline />
            {children}
        </ThemeProvider>
    );
};

export default ThemeContextProvider;
